import { useState } from 'react';
import type { useFormVerification, VerificationChannel } from '../../hooks/useFormVerification';
import VerificationCodePrompt from '../Agent/VerificationCodePrompt';
import '../Agent/AgentChat.scss'; // the code prompt's card (.service-request-card), loaded the way the service form and the parts order load it
import './Verify.scss';

/** The slice of the verification hook the panel reads. The machine hub's verification (the same surface, bound to a sticker) fits it too. */
export type PanelVerification = Pick<
  ReturnType<typeof useFormVerification>,
  'phase' | 'channel' | 'maskedDestination' | 'canResend' | 'error' | 'requestCode' | 'submitCode' | 'resend' | 'reset'
>;

export interface VerifyPanelProps {
  verification: PanelVerification;
  phone: string;
  email: string;
  onPhoneChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  /** Text me or Email me, when the host keeps the choice (the shop sends it with its start). Without it the panel keeps its own, on text. */
  channel?: VerificationChannel;
  onChannelChange?: (channel: VerificationChannel) => void;
  /** A line above the toggle, in the error colour: the host's own words about why the panel is up. */
  notice?: string | null;
  heading?: string;
  subheading?: string;
}

/**
 * The verify-first panel the parts order, the machine hub and the shop share: the channel toggle picks where the one-time code goes
 * and shows only that channel's input, and once a code is out the code prompt takes its place. Phone and email stay separate
 * controlled values, so switching back keeps what was typed, and a value typed before switching still rides along as lead enrichment
 * (PartsOrderApp). Same layout as Step1Welcome's returning-customer block, so the rcv-* styles apply as-is.
 */
function VerifyPanel({
  verification, phone, email, onPhoneChange, onEmailChange, channel: controlled, onChannelChange, notice, heading, subheading,
}: VerifyPanelProps) {
  const [own, setOwn] = useState<VerificationChannel>('sms');
  const channel = controlled ?? own;
  const choose = (next: VerificationChannel) => {
    setOwn(next);
    onChannelChange?.(next);
  };

  const isVerifying = verification.phase === 'awaiting_code' || verification.phase === 'verifying';

  const handleSendCode = () => {
    verification.requestCode(channel, channel === 'sms' ? phone : email);
  };

  return (
    <div className="returning-customer-verify">
      {isVerifying ? (
        <VerificationCodePrompt
          channel={verification.channel}
          maskedDestination={verification.maskedDestination}
          canResend={verification.canResend}
          error={verification.error}
          disabled={verification.phase === 'verifying'}
          onSubmit={verification.submitCode}
          onResend={verification.resend}
          onSkip={verification.reset}
          skipLabel="Use a different phone or email"
        />
      ) : (
        <div className="rcv-start">
          {heading || subheading ? (
            <div className="rcv-start__header">
              {heading ? <strong>{heading}</strong> : null}
              {subheading ? <span>{subheading}</span> : null}
            </div>
          ) : null}
          {notice && <span className="error-text">{notice}</span>}
          <div className="rcv-channel-toggle">
            <button
              type="button"
              className={channel === 'sms' ? 'active' : ''}
              aria-pressed={channel === 'sms'}
              onClick={() => choose('sms')}
            >
              Text me
            </button>
            <button
              type="button"
              className={channel === 'email' ? 'active' : ''}
              aria-pressed={channel === 'email'}
              onClick={() => choose('email')}
            >
              Email me
            </button>
          </div>
          {channel === 'sms' ? (
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="(555) 123-4567"
              value={phone}
              onChange={(e) => onPhoneChange(e.target.value)}
              aria-label="Mobile phone for verification"
            />
          ) : (
            <input
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              aria-label="Email for verification"
            />
          )}
          {verification.error && <span className="error-text">{verification.error}</span>}
          <button
            type="button"
            className="rcv-send-btn"
            disabled={verification.phase === 'sending'}
            onClick={handleSendCode}
          >
            {verification.phase === 'sending' ? 'Sending…' : 'Send code'}
          </button>
        </div>
      )}
    </div>
  );
}

export default VerifyPanel;
